import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box, Typography } from '@mui/material';
import { filterByWindow } from '../statistics';
import { useTimeline } from '../TimelineContext';

export default function HeatmapWidget({ recordings = [], widgetConfig }) {
  const { window: timeWindow } = useTimeline();
  const recId = widgetConfig?.recId || '';
  const sensorKey = widgetConfig?.sensorKey || '';

  const rec = recordings.find((r) => r.id === recId);

  const { series, categories } = useMemo(() => {
    if (!rec?.data?.[sensorKey]) return { series: [], categories: [] };
    let points = rec.data[sensorKey];
    if (timeWindow) points = filterByWindow(points, timeWindow.start, timeWindow.end);
    if (points.length === 0) return { series: [], categories: [] };

    // Group by hour-of-day (rows) and date (columns)
    const grid = {}; // { date: { hour: [values] } }
    const dates = new Set();

    for (const dp of points) {
      const d = new Date(dp.time);
      const date = d.toLocaleDateString();
      const hour = d.getHours();
      dates.add(date);
      if (!grid[hour]) grid[hour] = {};
      if (!grid[hour][date]) grid[hour][date] = [];
      grid[hour][date].push(Number(dp.value));
    }

    const sortedDates = [...dates];
    const hours = Array.from({ length: 24 }, (_, i) => i);

    const series = hours.map((hour) => ({
      name: `${String(hour).padStart(2, '0')}:00`,
      data: sortedDates.map((date) => {
        const vals = grid[hour]?.[date];
        if (!vals || vals.length === 0) return { x: date, y: null };
        const avg = vals.reduce((s, v) => s + v, 0) / vals.length;
        return { x: date, y: Math.round(avg * 100) / 100 };
      }),
    })).reverse(); // top = 23:00, bottom = 00:00

    return { series, categories: sortedDates };
  }, [rec, sensorKey, timeWindow]);

  const chartOptions = {
    chart: { type: 'heatmap', toolbar: { show: false }, animations: { enabled: false } },
    dataLabels: { enabled: false },
    xaxis: { categories, labels: { rotate: -45, style: { fontSize: '9px' } } },
    plotOptions: {
      heatmap: { colorScale: { ranges: [
        { from: -Infinity, to: 0, color: '#00E400', name: 'Low' },
        { from: 0, to: 50, color: '#FFFF00', name: 'Moderate' },
        { from: 50, to: 150, color: '#FF7E00', name: 'High' },
        { from: 150, to: Infinity, color: '#FF0000', name: 'Very High' },
      ] } },
    },
    tooltip: { y: { formatter: (v) => v !== null ? v.toFixed(2) : 'No data' } },
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {series.length > 0 ? (
        <Box sx={{ flex: 1, overflow: 'hidden', px: 1 }}>
          <ReactApexChart type="heatmap" series={series} options={chartOptions} height="100%" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="body2" color="text.secondary">Select a recording and sensor</Typography>
        </Box>
      )}
    </Box>
  );
}
